// ── 관리자 정산서 관리 — 모바일 렌더 ─────────────────────────────────────────
// 기존 PC 패널 컴포넌트(PcLeftPanel/PcCenterPanel/PcRightPanel)를 탭으로 감쌈
// 별도 모바일 전용 UI 없이 이미 검증된 컴포넌트 그대로 재사용

import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { C } from './constants';
import { CHIP_BAR } from './pcStyles';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from '../adminlayout/designStandard';
import { SummaryHeader } from '../../../components/settlement/SummaryHeader';
import { PcLeftPanel }   from './PcLeftPanel';
import { PcCenterPanel } from './PcCenterPanel';
import { PcRightPanel }  from './PcRightPanel';

export function MobileView({
  // 마케터 선택
  marketers, selectedUser, setSelectedUser, mobileTab, setMobileTab,
  // 마케터 숨기기
  showHiddenMarketers, setShowHiddenMarketers, isMarketerHidden, toggleMarketerHidden,
  // 요약 수치
  summary, recTotal, netExpected, consultantInfo,
  // 좌 패널 (PcLeftPanel)
  marketerData, dataLoading, unmTotal, leftItems,
  leftTab, setLeftTab,
  selectedPay, setSelectedPay, handlePaymentClick, handleStoreCardClick, handleUnlink, linking,
  expandedId, setExpandedId,
  onEditShareRate,
  // 우 패널 (PcRightPanel)
  records, recLoading, editId, formDate, setFormDate,
  formAmt, formNote, setFormNote, saving,
  handleSave, resetForm, selectRecord, handleDelete, onAmtChange,
}) {
  const storesBadge   = marketerData.clients.length > 0 ? marketerData.clients.length : null;
  const timelineBadge = unmTotal > 0 ? `미연결 ${unmTotal}` : null;
  const settBadge     = records.length > 0 ? records.length : null;

  const tabs = [
    { key: 'stores',     label: '매장카드', badge: storesBadge,   warn: false },
    { key: 'timeline',   label: '날짜순',   badge: timelineBadge, warn: unmTotal > 0 },
    { key: 'settlement', label: '정산입금', badge: settBadge,     warn: false },
  ];

  // 탭 스타일 — adminsettlement/PcLeftPanel.tabSt와 동일 톤
  const tabBarSt = {
    display: 'flex', alignItems: 'center',
    background: 'var(--bg-card)',
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
  };
  const tabSt = (active) => ({
    flex: 1, padding: '12px 0',
    background: 'none', border: 'none',
    fontSize: 13, fontWeight: active ? 700 : 500,
    color: active ? C.accent : C.subDark, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
    borderBottom: active ? `2.5px solid ${C.accent}` : '2.5px solid transparent',
    transition: 'color 0.15s, border-color 0.15s',
  });
  const badgeSt = (active, warn) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 17, height: 17, borderRadius: 9,
    fontSize: 10, fontWeight: 700, padding: '0 4px',
    background: warn ? '#fee2e2' : active ? 'rgba(99,102,241,0.12)' : 'var(--bg-subtle)',
    color: warn ? '#dc2626' : active ? C.accent : C.subDark,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--bg)' }}>

      {/* ── 헤더: 표준 텍스트 헤더(카드 박스 제거) + 요약 수치 ── */}
      <div style={{ flexShrink: 0 }}>
        <div style={stdHeaderRow(true)}>
          <div>
            <h2 style={stdPageTitle}>
              정산서 관리
              {selectedUser && (
                <span style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, marginLeft: 8 }}>— {selectedUser.name}</span>
              )}
            </h2>
            <div style={stdPageSubtitle}>마케터 정산서를 관리합니다.</div>
          </div>
        </div>
        {selectedUser && (
          <div style={{ marginBottom: 8 }}>
            <SummaryHeader
              totalMinus={summary.totalMinus}
              totalPlus={summary.totalPlus}
              recTotal={recTotal}
              netExpected={netExpected}
              variant="row"
            />
          </div>
        )}
      </div>

      {/* ── 마케터 칩 바 ── */}
      {/* 모바일: 가로 스크롤, 1줄 고정 — 작업 영역 확보 (헤더 카드 제거 후 상단 구분선 추가) */}
      <div style={{ ...CHIP_BAR, flexShrink: 0, flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none', scrollbarWidth: 'none', borderTop: `1px solid ${C.border}` }}>
        {marketers.length === 0 ? (
          <span style={{ fontSize: 12, color: C.sub }}>배정된 마케터가 없습니다.</span>
        ) : (() => {
          const hiddenCount = marketers.filter((mk) => isMarketerHidden?.(mk)).length;
          const visible = marketers.filter((mk) => showHiddenMarketers || !isMarketerHidden?.(mk));
          return (
            <>
              {visible.map((mk) => {
                const active = selectedUser?.id === mk.id;
                const hidden = isMarketerHidden?.(mk);
                return (
                  <span
                    key={mk.id}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      padding: '3px 5px 3px 12px', borderRadius: 20,
                      border: active ? '2px solid #2563eb' : `1px solid ${C.border}`,
                      background: active ? 'rgba(37,99,235,0.10)' : 'var(--bg-card)',
                      opacity: hidden ? 0.5 : 1, flexShrink: 0, whiteSpace: 'nowrap',
                    }}
                  >
                    <button
                      onClick={() => { setSelectedUser(mk); setMobileTab('stores'); }}
                      style={{ fontSize: 12, fontWeight: active ? 700 : 500, border: 'none', background: 'none', padding: 0, color: active ? '#2563eb' : C.main, cursor: 'pointer' }}
                    >
                      {mk.name}{hidden ? ' (숨김)' : ''}
                    </button>
                    <button
                      onClick={() => toggleMarketerHidden?.(mk, !hidden)}
                      title={hidden ? '위젯에 다시 표시' : '위젯에서 숨기기'}
                      style={{ display: 'inline-flex', border: 'none', background: 'none', padding: 2, cursor: 'pointer', color: hidden ? '#2563eb' : C.sub }}
                    >
                      {hidden ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                  </span>
                );
              })}
              {(hiddenCount > 0 || showHiddenMarketers) && (
                <button
                  onClick={() => setShowHiddenMarketers?.((v) => !v)}
                  style={{ fontSize: 11, fontWeight: 600, padding: '4px 11px', borderRadius: 20, flexShrink: 0, whiteSpace: 'nowrap', border: `1px dashed ${C.border}`, background: showHiddenMarketers ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)', color: showHiddenMarketers ? '#2563eb' : C.sub, cursor: 'pointer' }}
                >
                  {showHiddenMarketers ? '숨긴마케터 숨기기' : `숨긴마케터 같이보기${hiddenCount ? ` (${hiddenCount})` : ''}`}
                </button>
              )}
            </>
          );
        })()}
      </div>

      {/* ── 탭 바 ── */}
      <div style={tabBarSt}>
        {tabs.map(({ key, label, badge, warn }) => {
          const active = mobileTab === key;
          return (
            <button key={key} style={tabSt(active)} onClick={() => setMobileTab(key)}>
              {label}
              {badge != null && <span style={badgeSt(active, warn)}>{badge}</span>}
            </button>
          );
        })}
      </div>

      {/* ── 탭 콘텐츠 — 기존 PC 패널 컴포넌트 그대로 재사용 ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {mobileTab === 'stores' && (
          <PcLeftPanel
            selectedUser={selectedUser}
            dataLoading={dataLoading}
            marketerData={marketerData}
            unmTotal={unmTotal}
            leftItems={leftItems}
            leftTab={leftTab}
            setLeftTab={setLeftTab}
            selectedPay={selectedPay}
            setSelectedPay={setSelectedPay}
            handlePaymentClick={handlePaymentClick}
            handleStoreCardClick={handleStoreCardClick}
            handleUnlink={handleUnlink}
            linking={linking}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            onEditShareRate={onEditShareRate}
          />
        )}
        {mobileTab === 'timeline' && (
          <PcCenterPanel
            selectedUser={selectedUser}
            dataLoading={dataLoading}
            marketerData={marketerData}
            records={records}
            unmTotal={unmTotal}
          />
        )}
        {mobileTab === 'settlement' && (
          <PcRightPanel
            selectedUser={selectedUser}
            recLoading={recLoading}
            records={records}
            recTotal={recTotal}
            editId={editId}
            formDate={formDate}
            setFormDate={setFormDate}
            formAmt={formAmt}
            formNote={formNote}
            setFormNote={setFormNote}
            saving={saving}
            handleSave={handleSave}
            resetForm={resetForm}
            selectRecord={selectRecord}
            handleDelete={handleDelete}
            onAmtChange={onAmtChange}
          />
        )}
      </div>

    </div>
  );
}
